import React from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";
import Alert from "@mui/material/Alert";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import WarningIcon from "@mui/icons-material/Warning";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import format from "date-fns/format";
import differenceInDays from "date-fns/differenceInDays";
import { Permit } from "common/types/permits";
import PermitStatusBadge from "../PermitStatusBadge";
import { OperatorInformationSection } from "../shared/OperatorInformationSection";
import { VehiclesListSection } from "../shared/VehiclesListSection";
import { DocumentsListSection, DocumentWithReviewStatus } from "../shared/DocumentsListSection";

export interface PermitDetailDialogContentProps {
  permit: Permit;
}

const formatDate = (dateString: string | null): string => {
  if (!dateString) {
    return "—";
  }
  return format(new Date(dateString), "MMM d, yyyy");
};

export const PermitDetailDialogContent: React.FC<PermitDetailDialogContentProps> = ({ permit }) => {
  const isExpiringSoon = permit.validTo
    ? differenceInDays(new Date(permit.validTo), new Date()) <= 30 &&
      differenceInDays(new Date(permit.validTo), new Date()) > 0
    : false;

  const denialReason =
    permit.applicationStatus === "denied" && permit.documentProposals?.[0]?.reviewComment
      ? permit.documentProposals[0].reviewComment
      : "Application has been denied.";

  let allDocuments: DocumentWithReviewStatus[] = [];
  if (permit.documentProposals && permit.documentProposals.length > 0) {
    allDocuments =
      permit.documentProposals[0].documents?.map((doc) => ({
        ...doc.document,
        reviewStatus: doc.reviewStatus,
      })) || [];
  }

  return (
    <>
      {/* Operator Information */}
      <OperatorInformationSection
        companyName={permit.payerAccount?.organizationName || null}
        accountResponsible={permit.applicationSubmittedByUser?.fullName || null}
        email={permit.applicationSubmittedByUser?.email || null}
        phoneNumber={permit.applicationSubmittedByUser?.phoneNumber || null}
      />

      <Divider sx={{ my: 2 }} />

      {/* Basic Information */}
      <Stack direction="column" spacing={2} mb={3}>
        <Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Permit ID
            </Typography>
            <Typography variant="body1" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
              {permit.displayId}
            </Typography>
          </Box>
          <PermitStatusBadge type="permit" status={permit.status} />
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary">
            Permit Type
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {permit.permitType?.name || "—"}
          </Typography>
        </Box>
      </Stack>

      <Divider sx={{ my: 2 }} />

      {/* Validity Period */}
      <Box mb={3}>
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <CalendarTodayIcon fontSize="small" color="action" />
          <Typography variant="subtitle2" fontWeight={600}>
            Validity Period
          </Typography>
        </Box>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Valid From
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(permit.validFrom)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="caption" color="text.secondary">
              Valid To
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body1" fontWeight={500}>
                {formatDate(permit.validTo)}
              </Typography>
              {isExpiringSoon && <Chip icon={<WarningIcon />} label="Expiring Soon" color="warning" size="small" />}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Approval Information (conditional) */}
      {permit.applicationStatus === "approved" && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box mb={3}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Approval Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Approved By
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {permit.applicationApprovedByUser?.fullName || permit.applicationApprovedByUser?.email || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">
                  Approved At
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formatDate(permit.applicationReviewedAt)}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
      )}

      {/* Vehicles List */}
      {permit.vehicles && permit.vehicles.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <VehiclesListSection vehicles={permit.vehicles} />
        </>
      )}

      {/* Denial Reason (conditional) */}
      {permit.applicationStatus === "denied" && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box mb={3}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              Denial Reason
            </Typography>
            <Alert severity="error">{denialReason}</Alert>
          </Box>
        </>
      )}

      {/* Documentation */}
      {allDocuments.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <DocumentsListSection documents={allDocuments} />
        </>
      )}
    </>
  );
};

export default PermitDetailDialogContent;
